import type { SessionStore } from '../auth/session.js'
// Форма описания ноды задана редактором: он её и потребляет, а клиент только
// объявляет, что отдаёт служба. Второе описание той же формы разошлось бы с
// первым (FR-NODE-001).
import type { NodeDefinitionView } from '../editor/definitions.js'
import {
  ApiClientError,
  type ApiKey,
  type AuthStep,
  type ChannelProvider,
  type Chat,
  type Contact,
  type CreatedApiKey,
  type Credential,
  type CredentialTypeDescriptor,
  type CreatedWebhook,
  type Delivery,
  type Execution,
  type ExecutionDetail,
  type Instance,
  type Message,
  type Page,
  type PageRequest,
  type Pipeline,
  type PipelineDetail,
  type PipelineVersion,
  type QrCode,
  type Role,
  type Session,
  type TokenSet,
  type User,
  type ValidationIssue,
  type Variable,
  type Webhook,
} from './types.js'

export { ApiClientError } from './types.js'

export interface AdminClient {
  login(email: string, password: string): Promise<Session>
  logout(): Promise<void>
  me(): Promise<{ id: string; email: string; tenantId: string | null; permissions: string[] }>

  instances(page?: PageRequest): Promise<Page<Instance>>
  createInstance(name: string, provider: ChannelProvider): Promise<Instance>
  submitAuth(id: string, step: AuthStep): Promise<void>
  startInstance(id: string): Promise<Instance>
  stopInstance(id: string): Promise<Instance>
  removeInstance(id: string): Promise<void>
  qr(id: string): Promise<QrCode | null>
  /**
   * Сбрасывает сохранённую авторизацию инстанса: следующий запуск покажет
   * новый QR. Ответ сервера — только факт выполнения, состояние авторизации
   * через API не отдаётся (FR-ADM-014).
   */
  resetInstance(id: string): Promise<void>

  webhooks(): Promise<Webhook[]>
  createWebhook(
    url: string,
    events: string[],
    instanceId?: string | null,
  ): Promise<CreatedWebhook>
  removeWebhook(id: string): Promise<void>
  deliveries(webhookId: string, page?: PageRequest): Promise<Page<Delivery>>

  chats(instanceId: string, page?: PageRequest): Promise<Page<Chat>>
  messages(chatId: string, page?: PageRequest): Promise<Page<Message>>
  /**
   * Содержимое вложения. Возвращается Blob, а не адрес: заголовок
   * авторизации в `<img>` и `<audio>` не поставить, поэтому вложение
   * качает клиент, а показывается оно по `blob:`-ссылке.
   */
  media(id: string): Promise<Blob>
  contacts(instanceId: string, page?: PageRequest): Promise<Page<Contact>>
  renameContact(id: string, name: string | null): Promise<Contact>

  users(): Promise<User[]>
  createUser(email: string, password: string): Promise<User>
  disableUser(id: string): Promise<void>
  enableUser(id: string): Promise<void>
  roles(): Promise<Role[]>
  createRole(name: string, permissions: string[]): Promise<Role>
  grantRole(userId: string, roleId: string): Promise<void>
  revokeRole(userId: string, roleId: string): Promise<void>
  permissions(): Promise<string[]>

  apiKeys(): Promise<ApiKey[]>
  createApiKey(name: string, scopes: string[]): Promise<CreatedApiKey>
  rotateApiKey(id: string): Promise<CreatedApiKey>
  revokeApiKey(id: string): Promise<void>

  /**
   * Нужна ли первичная настройка: в установке ещё нет ни одного
   * пользователя (FR-INST-014). Спрашивается до входа, поэтому без токена.
   */
  setupRequired(): Promise<boolean>
  completeSetup(input: {
    email: string
    password: string
    organization: string
    apiKeyName: string
  }): Promise<{ tenantId: string; apiKey: string; apiKeyPrefix: string }>

  pipelines(page?: PageRequest): Promise<Page<Pipeline>>
  createPipeline(name: string, description?: string): Promise<Pipeline>
  pipeline(id: string): Promise<PipelineDetail>
  /**
   * Удаляет сценарий вместе с версиями и журналом прогонов. Отказ приходит
   * с причиной: идущий прогон или ссылка обработчика падений (FR-PIPE-035).
   */
  removePipeline(id: string): Promise<void>
  saveDraft(id: string, graph: unknown): Promise<PipelineDetail>
  /**
   * Публикация. Непройденная валидация приходит отказом 422 со списком
   * замечаний в `details` — не сбоем службы, а состоянием графа
   * (FR-PIPE-003).
   */
  publishPipeline(id: string): Promise<PipelineVersion>
  /** Описания нод для палитры и формы параметров (FR-NODE-001). */
  nodeDefinitions(): Promise<NodeDefinitionView[]>

  credentials(): Promise<Credential[]>
  credentialTypes(): Promise<CredentialTypeDescriptor[]>
  createCredential(type: string, name: string, data: Record<string, string>): Promise<Credential>
  removeCredential(id: string): Promise<void>
  /** Пробный запрос. Ответ несёт исход, но не значение секрета (FR-CRED-004). */
  testCredential(id: string, probeUrl: string, method?: string): Promise<{ ok: boolean; message: string }>

  /**
   * Замечания валидатора к графу. Проверяет служба тем же валидатором, что
   * и публикация (FR-PIPE-004): вторая реализация на клиенте разошлась бы
   * с первой в первый же месяц.
   */
  validateGraph(pipelineId: string, graph: unknown): Promise<ValidationIssue[]>
  /** Тестовый прогон; `fromNodeId` начинает с выбранной ноды (FR-UI-004). */
  startExecution(pipelineId: string, input?: { fromNodeId?: string }): Promise<Execution>
  execution(id: string): Promise<ExecutionDetail>
  executions(
    pipelineId: string,
    page?: PageRequest & { status?: string },
  ): Promise<Page<Execution>>
  /** Повтор с шага по сохранённому входу этого шага (FR-PIPE-020, FR-UI-006). */
  retryFrom(executionId: string, nodeRunId: string): Promise<ExecutionDetail>
  cancelExecution(id: string): Promise<Execution>
  /** Опубликованные версии сценария с их графами (FR-PIPE-001, FR-UI-007). */
  pipelineVersions(pipelineId: string, page?: PageRequest): Promise<Page<PipelineVersion>>

  /** Переменные арендатора для выражений `{{ $vars.KEY }}` (FR-PIPE-031). */
  variables(): Promise<Variable[]>
  putVariable(key: string, value: string): Promise<Variable>
  removeVariable(key: string): Promise<void>
}

export interface CreateApiClientOptions {
  baseUrl: string
  session: SessionStore
  fetcher: typeof fetch
}

/** Разбирает тело ответа сервера в ошибку клиента, показывая его сообщение как есть (FR-ADM-006). */
async function toApiClientError(response: Response): Promise<ApiClientError> {
  let code = 'UNKNOWN_ERROR'
  let message = `Сервер ответил ошибкой (${response.status}).`
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string } }
    if (body.error?.code !== undefined) code = body.error.code
    if (body.error?.message !== undefined) message = body.error.message
  } catch {
    // Тело не JSON (например, обрыв соединения на прокси) — остаётся
    // сообщение по статусу, собранное выше.
  }
  return new ApiClientError(response.status, code, message)
}

function pageQuery(page?: PageRequest): string {
  if (page === undefined) return ''
  const params = new URLSearchParams()
  if (page.limit !== undefined) params.set('limit', String(page.limit))
  if (page.cursor !== undefined) params.set('cursor', page.cursor)
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

export function createApiClient(options: CreateApiClientOptions): AdminClient {
  const { baseUrl, session, fetcher } = options

  // Единственный разделяемый промис обновления: два параллельных отказа
  // не должны прокрутить ротацию refresh-токена дважды — второй оборот
  // аннулирует сессию (FR-SEC-005). Все запросы клиента ждут один и тот
  // же промис вместо того, чтобы каждый начинал своё обновление.
  let refreshing: Promise<void> | null = null

  function raw(path: string, init: RequestInit): Promise<Response> {
    return fetcher(`${baseUrl}${path}`, init)
  }

  async function doRefresh(): Promise<void> {
    const refreshToken = session.refreshToken()
    if (refreshToken === null) {
      session.clear()
      throw new ApiClientError(401, 'AUTH_ERROR', 'Сессия отсутствует, войдите снова.')
    }

    const response = await raw('/api/v2/auth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      // Обновление отвергнуто — продолжать с текущими токенами нельзя:
      // это как раз повторное использование отозванного refresh-токена
      // из FR-SEC-005, и сессия ему больше не доверяет.
      session.clear()
      throw await toApiClientError(response)
    }

    session.set((await response.json()) as TokenSet)
  }

  function refreshOnce(): Promise<void> {
    if (refreshing === null) {
      refreshing = doRefresh().finally(() => {
        refreshing = null
      })
    }
    return refreshing
  }

  /**
   * Запрос с токеном и одной попыткой обновления. Общий для JSON и для
   * двоичных ответов: двух разных правил обновления в клиенте быть не
   * должно — иначе вкладка, простоявшая дольше жизни токена, чинит себе
   * список сообщений и роняет скачивание вложения из того же списка.
   */
  async function send(path: string, init: RequestInit = {}, retried = false): Promise<Response> {
    const token = session.token()
    const headers: Record<string, string> = {}
    if (init.body !== undefined) headers['content-type'] = 'application/json'
    if (token !== null) headers.authorization = `Bearer ${token}`

    const response = await raw(path, { ...init, headers: { ...headers, ...init.headers } })

    if (response.status === 401 && !retried) {
      await refreshOnce()
      return send(path, init, true)
    }

    return response
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await send(path, init)

    if (!response.ok) {
      throw await toApiClientError(response)
    }

    if (response.status === 204) {
      return undefined as T
    }

    return (await response.json()) as T
  }

  async function login(email: string, password: string): Promise<Session> {
    const response = await raw('/api/v2/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!response.ok) throw await toApiClientError(response)
    const tokens = (await response.json()) as TokenSet

    // Права ещё не известны — «me» ниже требует токен, поэтому он обязан
    // быть в сессии уже сейчас, до того как права станут известны.
    session.set(tokens)
    const info = await me()
    const result: Session = { ...tokens, permissions: info.permissions }
    session.set(result)
    return result
  }

  async function logout(): Promise<void> {
    const refreshToken = session.refreshToken()
    try {
      if (refreshToken !== null) {
        await request('/api/v2/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        })
      }
    } finally {
      // Локальная сессия обнуляется независимо от того, ответил ли сервер:
      // человек нажал «выйти», и на его вкладке доступа быть не должно.
      session.clear()
    }
  }

  function me(): Promise<{ id: string; email: string; tenantId: string | null; permissions: string[] }> {
    return request('/api/v2/auth/me')
  }

  function instances(page?: PageRequest): Promise<Page<Instance>> {
    return request(`/api/v2/instances${pageQuery(page)}`)
  }

  /**
   * Подаёт шаг входа. Маршрут свой на каждый шаг: разбирать, что именно
   * прислали, по набору заполненных полей — тот же приём, от которого
   * отказались в отправке сообщений.
   */
  async function submitAuth(id: string, step: AuthStep): Promise<void> {
    const тело =
      step.kind === 'phone'
        ? JSON.stringify({ phone: step.phone })
        : step.kind === 'code'
          ? JSON.stringify({ code: step.code })
          : step.kind === 'password'
            ? JSON.stringify({ password: step.password })
            : undefined
    await request(`/api/v2/instances/${id}/auth/${step.kind}`, {
      method: 'POST',
      ...(тело === undefined ? {} : { body: тело }),
    })
  }

  function createInstance(name: string, provider: ChannelProvider): Promise<Instance> {
    return request('/api/v2/instances', {
      method: 'POST',
      body: JSON.stringify({ name, provider }),
    })
  }

  function startInstance(id: string): Promise<Instance> {
    return request(`/api/v2/instances/${id}/start`, { method: 'POST' })
  }

  function stopInstance(id: string): Promise<Instance> {
    return request(`/api/v2/instances/${id}/stop`, { method: 'POST' })
  }

  async function removeInstance(id: string): Promise<void> {
    await request(`/api/v2/instances/${id}`, { method: 'DELETE' })
  }

  async function resetInstance(id: string): Promise<void> {
    await request(`/api/v2/instances/${id}/reset-auth`, { method: 'POST' })
  }

  async function qr(id: string): Promise<QrCode | null> {
    try {
      return await request<QrCode>(`/api/v2/instances/${id}/qr`)
    } catch (error) {
      // Код ещё не выдан — это не отказ экрана, а обычное состояние
      // до запуска инстанса: сервер отвечает 404, а не пустым объектом.
      if (error instanceof ApiClientError && error.status === 404) return null
      throw error
    }
  }

  function webhooks(): Promise<Webhook[]> {
    return request<{ items: Webhook[] }>('/api/v2/webhooks').then((page) => page.items)
  }

  function createWebhook(
    url: string,
    events: string[],
    instanceId?: string | null,
  ): Promise<CreatedWebhook> {
    return request('/api/v2/webhooks', {
      method: 'POST',
      body: JSON.stringify(
        instanceId === undefined || instanceId === null
          ? { url, events }
          : { url, events, instanceId },
      ),
    })
  }

  async function removeWebhook(id: string): Promise<void> {
    await request(`/api/v2/webhooks/${id}`, { method: 'DELETE' })
  }

  function deliveries(webhookId: string, page?: PageRequest): Promise<Page<Delivery>> {
    return request(`/api/v2/webhooks/${webhookId}/deliveries${pageQuery(page)}`)
  }

  function chats(instanceId: string, page?: PageRequest): Promise<Page<Chat>> {
    return request(`/api/v2/instances/${instanceId}/chats${pageQuery(page)}`)
  }

  function messages(chatId: string, page?: PageRequest): Promise<Page<Message>> {
    return request(`/api/v2/chats/${chatId}/messages${pageQuery(page)}`)
  }

  async function media(id: string): Promise<Blob> {
    const response = await send(`/api/v2/media/${encodeURIComponent(id)}`)
    if (!response.ok) throw await toApiClientError(response)
    return response.blob()
  }

  function contacts(instanceId: string, page?: PageRequest): Promise<Page<Contact>> {
    return request(`/api/v2/instances/${instanceId}/contacts${pageQuery(page)}`)
  }

  function renameContact(id: string, name: string | null): Promise<Contact> {
    return request(`/api/v2/contacts/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) })
  }

  function users(): Promise<User[]> {
    return request<{ items: User[] }>('/api/v2/users').then((page) => page.items)
  }

  function createUser(email: string, password: string): Promise<User> {
    return request('/api/v2/users', { method: 'POST', body: JSON.stringify({ email, password }) })
  }

  async function disableUser(id: string): Promise<void> {
    await request(`/api/v2/users/${id}/disable`, { method: 'POST' })
  }

  // Без этого метода отключение из портала необратимо: маршрут на сервере
  // есть (routes/users.ts), но клиенту нечем было до него достучаться —
  // включить отключённого было нечем (FR-ADM-011).
  async function enableUser(id: string): Promise<void> {
    await request(`/api/v2/users/${id}/enable`, { method: 'POST' })
  }

  function roles(): Promise<Role[]> {
    return request<{ items: Role[] }>('/api/v2/roles').then((page) => page.items)
  }

  function createRole(name: string, permissionNames: string[]): Promise<Role> {
    return request('/api/v2/roles', {
      method: 'POST',
      body: JSON.stringify({ name, permissions: permissionNames }),
    })
  }

  async function grantRole(userId: string, roleId: string): Promise<void> {
    await request(`/api/v2/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ roleId }),
    })
  }

  async function revokeRole(userId: string, roleId: string): Promise<void> {
    await request(`/api/v2/users/${userId}/roles/${roleId}`, { method: 'DELETE' })
  }

  function permissionsCatalog(): Promise<string[]> {
    return request<{ items: string[] }>('/api/v2/permissions').then((page) => page.items)
  }

  function apiKeys(): Promise<ApiKey[]> {
    return request<{ items: ApiKey[] }>('/api/v2/api-keys').then((page) => page.items)
  }

  function createApiKey(name: string, scopes: string[]): Promise<CreatedApiKey> {
    return request('/api/v2/api-keys', { method: 'POST', body: JSON.stringify({ name, scopes }) })
  }

  function rotateApiKey(id: string): Promise<CreatedApiKey> {
    return request(`/api/v2/api-keys/${id}/rotate`, { method: 'POST' })
  }

  async function revokeApiKey(id: string): Promise<void> {
    await request(`/api/v2/api-keys/${id}`, { method: 'DELETE' })
  }

  async function setupRequired(): Promise<boolean> {
    const answer = await request<{ required: boolean }>('/api/v2/system/setup')
    return answer.required
  }

  function completeSetup(input: {
    email: string
    password: string
    organization: string
    apiKeyName: string
  }): Promise<{ tenantId: string; apiKey: string; apiKeyPrefix: string }> {
    return request('/api/v2/system/setup', { method: 'POST', body: JSON.stringify(input) })
  }

  function pipelines(page?: PageRequest): Promise<Page<Pipeline>> {
    return request(`/api/v2/pipelines${pageQuery(page)}`)
  }

  function createPipeline(name: string, description?: string): Promise<Pipeline> {
    return request('/api/v2/pipelines', {
      method: 'POST',
      body: JSON.stringify(description === undefined ? { name } : { name, description }),
    })
  }

  function pipeline(id: string): Promise<PipelineDetail> {
    return request(`/api/v2/pipelines/${id}`)
  }

  async function removePipeline(id: string): Promise<void> {
    await request(`/api/v2/pipelines/${id}`, { method: 'DELETE' })
  }

  function saveDraft(id: string, graph: unknown): Promise<PipelineDetail> {
    return request(`/api/v2/pipelines/${id}/draft`, { method: 'PUT', body: JSON.stringify({ graph }) })
  }

  function publishPipeline(id: string): Promise<PipelineVersion> {
    return request<{ version: PipelineVersion }>(`/api/v2/pipelines/${id}/publish`, {
      method: 'POST',
    }).then((body) => body.version)
  }

  function nodeDefinitions(): Promise<NodeDefinitionView[]> {
    return request<{ items: NodeDefinitionView[] }>('/api/v2/node-definitions').then(
      (page) => page.items,
    )
  }

  function credentials(): Promise<Credential[]> {
    return request<{ items: Credential[] }>('/api/v2/credentials').then((page) => page.items)
  }

  function credentialTypes(): Promise<CredentialTypeDescriptor[]> {
    return request<{ items: CredentialTypeDescriptor[] }>('/api/v2/credential-types').then(
      (page) => page.items,
    )
  }

  function createCredential(
    type: string,
    name: string,
    data: Record<string, string>,
  ): Promise<Credential> {
    return request('/api/v2/credentials', {
      method: 'POST',
      body: JSON.stringify({ type, name, data }),
    })
  }

  async function removeCredential(id: string): Promise<void> {
    await request(`/api/v2/credentials/${id}`, { method: 'DELETE' })
  }

  function testCredential(
    id: string,
    probeUrl: string,
    method?: string,
  ): Promise<{ ok: boolean; message: string }> {
    return request(`/api/v2/credentials/${id}/test`, {
      method: 'POST',
      body: JSON.stringify(method === undefined ? { probeUrl } : { probeUrl, method }),
    })
  }

  function validateGraph(pipelineId: string, graph: unknown): Promise<ValidationIssue[]> {
    return request<{ issues: ValidationIssue[] }>(`/api/v2/pipelines/${pipelineId}/validate`, {
      method: 'POST',
      body: JSON.stringify({ graph }),
    }).then((body) => body.issues)
  }

  function startExecution(pipelineId: string, input: { fromNodeId?: string } = {}): Promise<Execution> {
    return request(`/api/v2/pipelines/${pipelineId}/executions`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  }

  function execution(id: string): Promise<ExecutionDetail> {
    return request(`/api/v2/executions/${id}`)
  }

  function executions(
    pipelineId: string,
    page: PageRequest & { status?: string } = {},
  ): Promise<Page<Execution>> {
    const params = new URLSearchParams()
    if (page.limit !== undefined) params.set('limit', String(page.limit))
    if (page.cursor !== undefined) params.set('cursor', page.cursor)
    if (page.status !== undefined) params.set('status', page.status)
    const query = params.toString()
    return request(`/api/v2/pipelines/${pipelineId}/executions${query === '' ? '' : `?${query}`}`)
  }

  function retryFrom(executionId: string, nodeRunId: string): Promise<ExecutionDetail> {
    return request(`/api/v2/executions/${executionId}/retry-from/${nodeRunId}`, { method: 'POST' })
  }

  function cancelExecution(id: string): Promise<Execution> {
    return request(`/api/v2/executions/${id}/cancel`, { method: 'POST' })
  }

  function pipelineVersions(pipelineId: string, page?: PageRequest): Promise<Page<PipelineVersion>> {
    return request(`/api/v2/pipelines/${pipelineId}/versions${pageQuery(page)}`)
  }

  function variables(): Promise<Variable[]> {
    return request<{ items: Variable[] }>('/api/v2/variables').then((page) => page.items)
  }

  function putVariable(key: string, value: string): Promise<Variable> {
    return request(`/api/v2/variables/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    })
  }

  async function removeVariable(key: string): Promise<void> {
    await request(`/api/v2/variables/${encodeURIComponent(key)}`, { method: 'DELETE' })
  }

  return {
    login,
    logout,
    me,
    instances,
    createInstance,
    submitAuth,
    startInstance,
    stopInstance,
    removeInstance,
    qr,
    resetInstance,
    webhooks,
    createWebhook,
    removeWebhook,
    deliveries,
    chats,
    messages,
    media,
    contacts,
    renameContact,
    users,
    createUser,
    disableUser,
    enableUser,
    roles,
    createRole,
    grantRole,
    revokeRole,
    permissions: permissionsCatalog,
    apiKeys,
    createApiKey,
    rotateApiKey,
    revokeApiKey,
    pipelines,
    setupRequired,
    completeSetup,
    createPipeline,
    pipeline,
    removePipeline,
    saveDraft,
    publishPipeline,
    nodeDefinitions,
    credentials,
    credentialTypes,
    createCredential,
    removeCredential,
    testCredential,
    validateGraph,
    startExecution,
    execution,
    executions,
    retryFrom,
    cancelExecution,
    pipelineVersions,
    variables,
    putVariable,
    removeVariable,
  }
}
